import { useQueryClient } from '@tanstack/react-query';
import moment from 'moment';
import { Formik } from 'formik';
import classNames from 'classnames';
import pinterpolate from 'pinterpolate';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { RouteComponentProps } from 'react-router-dom';
import type { ConfirmEngagementStatus } from 'interface/engagementStatus';
import type { EmployeeLike } from 'interface/userUtils';
import type { DropdownOption } from 'components/common/dropDown/Dropdown';

import {
  GENDER,
  REACT_DATEPICKER_DATE_FORMAT,
  CURRENT_ENGAGEMENT_STATUS_NONE,
  TERMINATION_CONFIRMATION_TITLE,
  CHANGE_ENGAGEMENT_STATUS_PAGE_TITLE,
  ENGAGEMENT_STATUS_UPDATE_SUCCESS_MESSAGE,
  ENGAGEMENT_STATUS_LEAVE_CREDIT_FAILED_MESSAGE,
  ENGAGEMENT_STATUS_LEAVE_CREDIT_SUCCESS_MESSAGE,
} from 'constants/appConstants';
import { EMPLOYEE_PROFILE } from 'constants/routes';
import { DEFAULT_DATE_FORMAT } from 'constants/date';
import { TERMINATED } from 'constants/employeeStatus';
import { ENGAGEMENT_STATUS_IDS } from 'constants/employeeStatus';
import { PERMANENT, PROBATION } from 'constants/engagementStatus';
import { leaveSource, leaveTypeId, leaveTypeLabel } from 'constants/leave';

import history from 'utils/history';
import { success, error } from 'utils/toast';
import { extractFullName } from 'utils/string';
import { handleError } from 'utils/errorHandler';
import { getFormattedDate, getMonthsLaterDate, isSubContractEndDateOutsideRange } from 'utils/date';

import { useEngagementStatusesQuery } from 'hooks/query/useLookupQueries';
import { useFetchUserByIdQuery, useUpdateEngagementStatusMutation } from 'hooks/query/useUserQueries';
import { calculateLeaveQueryOptions, useSaveLeaveCreditsMutation } from 'hooks/query/useLeaveQueries';
import { useFetchFiscalYearByCountry } from 'hooks/query/useFetchFiscalYearByCountry';

import { calendar } from 'assets/images';
import DropDown from 'components/common/dropDown';
import Loading from 'components/common/loading/Loading';
import InputWrapper from 'components/common/inputWrapper';
import DatePicker from 'components/common/datepicker/DatePicker';
import ProfileHeader from '../components/profileHeader/ProfileHeader';

import changeEngagementStatusSchema from 'schemas/changeEngagementStatusSchema';
import ConfirmationModal from '../changeDesignation/components/ConfirmationModal';
import EngagementStatusList from '../../components/employeeForm/forms/leapfrogHistory/components/EngagementStatusList';

import EmployeeStatusDetails from './components/EmployeeStatusDetails';
import TerminationRestrictionInfo from './components/TerminationRestrictionInfo';
import TerminationConfirmationModal from './components/TerminationConfirmationModal';

interface ChangeEngagementProps extends RouteComponentProps<{ id?: string }> {
  user: EmployeeLike;
  match: { params: { id?: string } };
}

interface EngagementStatusEntity {
  id: number;
  name: string;
}

interface EngagementHistoryRow {
  id: number;
  transitionDate: string;
  endDate?: string | null;
  engagementStatus: EngagementStatusEntity;
  empStatusDetails?: Array<{ category: string; date: string }>;
}

interface ChangeEngagementEmployee {
  id: number;
  firstName: string;
  lastName: string;
  middleName?: string | null;
  country?: string;
  gender?: string;
  currentEngagementStatusId: number;
  currentEngagementStatusName: string;
  currentTransitionDate: string;
  currentEndDate?: string | null;
  currentEmpStatusId: number;
  name: string;
  empStatusHistory: EngagementHistoryRow[];
  resignation?: { id?: number };
  empStatus?: string;
  joinDate?: string;
  designation?: { name?: string; area?: { name?: string } };
}

interface EngagementPayload {
  engagementStatusId: number;
  transitionDate: string;
  endDate?: string;
  empStatusDetails?: Array<{ id?: number; category: string; date?: string }>;
  [key: string]: number | string | undefined | Array<{ id?: number; category: string; date?: string }>;
}

interface ChangeEngagementFormValues {
  engagementStatusId: number | null;
  engagementStatusName?: string;
  transitionDate: moment.Moment;
  endDate: moment.Moment | null;
  empStatusDetails: Array<{ id?: number; category: string; date?: string }>;
}

const collectValidationMessages = (value: unknown): string[] => {
  if (typeof value === 'string' && value.trim()) {
    return [value];
  }

  if (Array.isArray(value)) {
    return value.flatMap((item) => collectValidationMessages(item));
  }

  if (value && typeof value === 'object') {
    return Object.values(value as Record<string, unknown>).flatMap((item) => collectValidationMessages(item));
  }

  return [];
};

const DEFAULT_ENGAGEMENT_EMPLOYEE: ChangeEngagementEmployee = {
  id: 0,
  firstName: '',
  lastName: '',
  currentEngagementStatusId: 0,
  currentEngagementStatusName: CURRENT_ENGAGEMENT_STATUS_NONE,
  currentTransitionDate: '',
  currentEmpStatusId: 0,
  name: '',
  empStatusHistory: [],
};

const getPageTitle = (employeeData: ChangeEngagementEmployee) => {
  const employeeName = employeeData ? extractFullName(employeeData) : '';

  return employeeName ? `${employeeName}'s engagement status` : CHANGE_ENGAGEMENT_STATUS_PAGE_TITLE;
};

const ChangeEngagement: React.FC<ChangeEngagementProps> = ({
  user,
  match: {
    params: { id },
  },
}) => {
  const [isFetchingLeaveData, setIsFetchingLeaveData] = useState(false);
  const [openConfirmationModal, setOpenConfirmationModal] = useState(false);
  const [payload, setPayload] = useState<EngagementPayload | null>(null);
  const [leave, setLeave] = useState<ConfirmEngagementStatus | null>(null);
  const [openTerminationConfirmationModal, setOpenTerminationConfirmationModal] = useState(false);
  const [isTerminatedSelected, setIsTerminatedSelected] = useState(false);
  const [isNotAllowedToTerminate, setIsNotAllowedToTerminate] = useState(false);

  const { data: employeeResponse, isLoading: isLoadingEmployee } = useFetchUserByIdQuery<ChangeEngagementEmployee>(
    id,
    Boolean(id)
  );
  const employee = useMemo(() => {
    if (!employeeResponse) {
      return DEFAULT_ENGAGEMENT_EMPLOYEE;
    }

    const empStatusHistory = employeeResponse.empStatusHistory ?? [];

    if (empStatusHistory.length === 0) {
      return {
        ...employeeResponse,
        name: extractFullName(employeeResponse),
        currentEmpStatusId: 0,
        currentEngagementStatusName: CURRENT_ENGAGEMENT_STATUS_NONE,
        currentEngagementStatusId: 0,
        currentTransitionDate: '',
        currentEndDate: undefined,
      };
    }

    const recentEmpStatus = empStatusHistory.reduce((acc, cur) => (cur.id > acc.id ? cur : acc));

    return {
      ...employeeResponse,
      name: extractFullName(employeeResponse),
      currentEmpStatusId: recentEmpStatus.id,
      currentEngagementStatusName: recentEmpStatus.engagementStatus.name || CURRENT_ENGAGEMENT_STATUS_NONE,
      currentEngagementStatusId: recentEmpStatus.engagementStatus.id,
      currentTransitionDate: recentEmpStatus.transitionDate,
      currentEndDate: recentEmpStatus.endDate,
    };
  }, [employeeResponse]);
  const title = useMemo(
    () => (employeeResponse ? getPageTitle(employeeResponse) : CHANGE_ENGAGEMENT_STATUS_PAGE_TITLE),
    [employeeResponse]
  );
  const { data: engagementStatuses = [], isLoading: isLoadingEngagementStatus } = useEngagementStatusesQuery();
  const updateEngagementStatusMutation = useUpdateEngagementStatusMutation();
  const saveLeaveCreditsMutation = useSaveLeaveCreditsMutation();
  const queryClient = useQueryClient();
  const isSubmitting = updateEngagementStatusMutation.isPending;
  const {
    data: fiscalYearData = [],
    isLoading: isFetchingFiscalYear,
    isError: isFiscalYearError,
  } = useFetchFiscalYearByCountry(employee.country ?? '', Boolean(employee.country));

  const currentFiscalYear = useMemo(() => {
    if (!employee.country || isFetchingFiscalYear || isFiscalYearError) {
      return null;
    }

    const fiscalYears = fiscalYearData as Array<{ startDate: string; endDate: string; isCurrent?: boolean }>;

    return fiscalYears.reduce(
      (accu, curr) => (curr.isCurrent ? curr : accu),
      fiscalYears[0] || { startDate: '', endDate: '', isCurrent: false }
    );
  }, [employee.country, fiscalYearData, isFetchingFiscalYear, isFiscalYearError]);

  const engagementStatusOptions = useMemo(
    () =>
      engagementStatuses.map((item) => ({
        label: item.name,
        value: item.id,
      })),
    [engagementStatuses]
  );

  const initialTransitionDate = useMemo(() => {
    const today = moment(getFormattedDate()).startOf('day');
    const currentTransitionDate = moment(employee.currentTransitionDate || undefined).startOf('day');
    const fiscalYearStartDate = moment(currentFiscalYear?.startDate || undefined).startOf('day');

    let nextTransitionDate = today;

    if (currentTransitionDate.isValid() && currentTransitionDate.isAfter(nextTransitionDate)) {
      nextTransitionDate = currentTransitionDate;
    }

    if (fiscalYearStartDate.isValid() && fiscalYearStartDate.isAfter(nextTransitionDate)) {
      nextTransitionDate = fiscalYearStartDate;
    }

    return nextTransitionDate;
  }, [currentFiscalYear?.startDate, employee.currentTransitionDate]);

  const initialValues: ChangeEngagementFormValues = {
    engagementStatusId: null,
    transitionDate: initialTransitionDate,
    endDate: null,
    empStatusDetails: [],
  };

  const isButtonDisabled = isSubmitting || isFetchingLeaveData || (isNotAllowedToTerminate && isTerminatedSelected);

  useEffect(() => {
    window.document.title = title;
  }, [title]);

  const redirectToEmployeeEdit = useCallback(() => {
    if (!id) {
      return;
    }

    history.push(pinterpolate(EMPLOYEE_PROFILE, { id }));
  }, [id]);

  const showTerminationConfirmationModal = () => {
    setOpenTerminationConfirmationModal(true);
  };

  const closeTerminationConfirmationModal = () => {
    setOpenTerminationConfirmationModal(false);
  };

  const changeEngagementStatus = useCallback(
    (engagementPayload: EngagementPayload, successCallback: (() => void) | null = null) => {
      //first we will try to close the termination confirmation modal before proceeding.
      closeTerminationConfirmationModal();

      updateEngagementStatusMutation.mutate(
        { id: id!, payload: engagementPayload },
        {
          onSuccess: () => {
            success({
              title: 'Success',
              message: ENGAGEMENT_STATUS_UPDATE_SUCCESS_MESSAGE,
            });

            if (successCallback) {
              successCallback();
            }

            redirectToEmployeeEdit();
          },
        }
      );
    },
    [id, redirectToEmployeeEdit, updateEngagementStatusMutation]
  );

  const showConfirmationModalWithLeave = () => {
    setOpenConfirmationModal(true);
  };

  const fetchLeaveData = useCallback(
    async (engagementPayload: EngagementPayload) => {
      try {
        setIsFetchingLeaveData(true);

        const leaveParams = {
          gender: employee.gender,
          currentEngagementStatusId: employee.currentEngagementStatusId,
          nextEngagementStatusId: engagementPayload.engagementStatusId,
          transitionDate: engagementPayload.transitionDate,
          endDate: engagementPayload.endDate,
          userId: employee.id,
        };

        const { data } = (await queryClient.fetchQuery(calculateLeaveQueryOptions(leaveParams))) as {
          data: ConfirmEngagementStatus;
        };

        setLeave({
          discretionaryLeave: data.discretionaryLeave,
          sickLeave: data.sickLeave,
          mensurationLeave: data.mensurationLeave,
        });

        showConfirmationModalWithLeave();
      } catch (error) {
        handleError(error);
      } finally {
        setIsFetchingLeaveData(false);
      }
    },
    [employee, queryClient]
  );

  const handleSubmit = async ({
    engagementStatusId,
    transitionDate,
    empStatusDetails,
    endDate,
  }: ChangeEngagementFormValues) => {
    const nextPayload: EngagementPayload = {
      engagementStatusId,
      transitionDate: transitionDate.startOf('day').format(DEFAULT_DATE_FORMAT),
      ...(endDate && { endDate: endDate.startOf('day').format(DEFAULT_DATE_FORMAT) }),
      ...(empStatusDetails && { empStatusDetails }),
    };

    setPayload(nextPayload);

    if (employee.currentEngagementStatusId === PROBATION && nextPayload.engagementStatusId === PERMANENT) {
      await fetchLeaveData(nextPayload);
      return;
    }

    if (nextPayload.engagementStatusId === ENGAGEMENT_STATUS_IDS.TERMINATED) {
      showTerminationConfirmationModal();
      return;
    }

    await changeEngagementStatus(nextPayload);
  };

  const getLabelOfEngagementStatusFromOptions = (engagementStatusId?: number | null) =>
    engagementStatusOptions &&
    engagementStatusOptions.filter((status) => status.value === engagementStatusId)[0]?.label;

  const updateLeaveCredits = (leaveCreditPayload: {
    userId: string | number | undefined;
    leaveDays: number;
    leaveTypeId: number;
    leaveSource: string;
  }) => {
    saveLeaveCreditsMutation.mutate(leaveCreditPayload, {
      onSuccess: () => {
        success({
          title: 'Success',
          message: pinterpolate(ENGAGEMENT_STATUS_LEAVE_CREDIT_SUCCESS_MESSAGE, {
            leaveType: leaveTypeLabel[leaveCreditPayload.leaveTypeId],
          }),
        });
      },
      onError: () => {
        error({
          title: 'Error',
          message: pinterpolate(ENGAGEMENT_STATUS_LEAVE_CREDIT_FAILED_MESSAGE, {
            leaveType: leaveTypeLabel[leaveCreditPayload.leaveTypeId],
          }),
        });
      },
    });
  };

  const handleConfirmation = async ({ discretionaryLeave, sickLeave, mensurationLeave }) => {
    if (!payload) {
      return;
    }

    await changeEngagementStatus(payload, () => {
      if (discretionaryLeave > 0) {
        updateLeaveCredits({
          userId: id,
          leaveDays: discretionaryLeave,
          leaveTypeId: leaveTypeId.DISCRETIONARY,
          leaveSource: leaveSource.ENGAGEMENT_STATUS_TRANSITION,
        });
      }

      if (sickLeave > 0) {
        updateLeaveCredits({
          userId: id,
          leaveDays: sickLeave,
          leaveTypeId: leaveTypeId.SICK,
          leaveSource: leaveSource.ENGAGEMENT_STATUS_TRANSITION,
        });
      }

      if (employee.gender === GENDER.FEMALE && mensurationLeave > 0) {
        updateLeaveCredits({
          userId: id,
          leaveDays: mensurationLeave,
          leaveTypeId: leaveTypeId.MENSURATION,
          leaveSource: leaveSource.ENGAGEMENT_STATUS_TRANSITION,
        });
      }
    });
  };

  const setEndDate = (
    id: number | null,
    setFieldTouched: (field: string, touched: boolean, shouldValidate?: boolean) => void,
    setFieldValue: (field: string, value: moment.Moment | null | [] | number | string) => void,
    startDate: moment.Moment
  ) => {
    if (id === ENGAGEMENT_STATUS_IDS.FIXED_TERM_CONTRACT) {
      setFieldTouched('endDate', true, true);
      setFieldValue('endDate', moment(getMonthsLaterDate(startDate.format(DEFAULT_DATE_FORMAT), 6)));
    } else if (id === ENGAGEMENT_STATUS_IDS.SUB_CONTRACT) {
      setFieldTouched('endDate', false, true);
      setFieldValue('endDate', null);
    } else {
      setFieldTouched('endDate', false, true);
      setFieldValue('endDate', null);
    }
  };

  const latestEmpStatus =
    employee?.empStatusHistory?.length && employee.empStatusHistory[employee.empStatusHistory.length - 1];

  const isEndDateOutsideRange = (date: moment.Moment, transitionDate: moment.Moment | string) =>
    isSubContractEndDateOutsideRange(date, moment(transitionDate), moment(employee.currentTransitionDate));

  useEffect(() => {
    if (employee.country && isFiscalYearError) {
      handleError(new Error('Failed to fetch fiscal year'));
      redirectToEmployeeEdit();
    }
  }, [employee.country, isFiscalYearError, redirectToEmployeeEdit]);

  if (isLoadingEmployee || isLoadingEngagementStatus || isFetchingFiscalYear) {
    return (
      <div className="container loading-container">
        <Loading />
      </div>
    );
  }

  return (
    <>
      <TerminationConfirmationModal
        isOpen={openTerminationConfirmationModal}
        title={{
          text: TERMINATION_CONFIRMATION_TITLE,
          type: `danger`,
        }}
        firstName={employee.firstName}
        lastName={employee.lastName}
        onConfirm={() => {
          if (payload) {
            void changeEngagementStatus(payload);
          }
        }}
        onCancel={closeTerminationConfirmationModal}
      />
      <ConfirmationModal
        isSubmitting={isSubmitting}
        displayMensurationLeaveField={employee.gender === GENDER.FEMALE}
        discretionaryLeave={leave?.discretionaryLeave}
        sickLeave={leave?.sickLeave}
        mensurationLeave={leave?.mensurationLeave}
        isOpen={openConfirmationModal}
        currentEngagementStatusLabel={employee.currentEngagementStatusName.toLowerCase()}
        newEngagementStatusLabel={getLabelOfEngagementStatusFromOptions(payload?.engagementStatusId)?.toLowerCase()}
        onConfirm={handleConfirmation}
        onCancel={() => setOpenConfirmationModal(false)}
      />
      <div className="title profile-wrapper">
        <ProfileHeader employee={employee} user={{ id: Number(user.id) }} />
      </div>
      <div className="container d-flex fd-row flex-fix name-wrap">
        <div className="full-scope-card momr-20">
          <div className="full-scope-card__header table-header name-wrap">
            <div className="d-flex flex-row">
              <h3>{CHANGE_ENGAGEMENT_STATUS_PAGE_TITLE}</h3>
            </div>
          </div>
          <div className="engagement-manager-form">
            <Formik
              initialValues={{ ...initialValues }}
              onSubmit={handleSubmit}
              validationSchema={changeEngagementStatusSchema({
                currentEngagementStatusId: employee.currentEngagementStatusId,
                currentTransitionDate: employee.currentTransitionDate,
              })}
            >
              {({ handleSubmit, errors, values, setFieldValue, touched, setFieldTouched, submitCount }) => {
                const validationMessages = collectValidationMessages(errors);

                return (
                  <form onSubmit={handleSubmit}>
                    <label className="font-label font-14 label-margin">
                      <span className="font-weight-bold dark--text"> Current Employment Status: </span>
                      <span className="span-padding-left">{employee.currentEngagementStatusName}</span>
                    </label>

                    <InputWrapper
                      label="Employment Status"
                      error={
                        touched.engagementStatusId && typeof errors.engagementStatusId === 'string'
                          ? errors.engagementStatusId
                          : ''
                      }
                      isMandatory
                    >
                      <DropDown
                        hasError={Boolean(touched.engagementStatusId && errors.engagementStatusId)}
                        isDisabled={isSubmitting || isFetchingLeaveData}
                        isSearchable={true}
                        name="engagementStatusId"
                        onDropDownChange={(selected) => {
                          if (!selected || Array.isArray(selected)) {
                            return;
                          }
                          const { value } = selected as DropdownOption;
                          setFieldTouched('engagementStatusId', true, true);
                          const selectedStatus = (engagementStatusOptions || []).find(
                            (status) => status.value === Number(value)
                          );
                          setFieldValue('engagementStatusName', selectedStatus?.label || '');
                          setFieldValue('empStatusDetails', []);

                          setEndDate(Number(value), setFieldTouched, setFieldValue, values.transitionDate);
                          setFieldValue('engagementStatusId', Number(value));

                          setIsTerminatedSelected(value === ENGAGEMENT_STATUS_IDS.TERMINATED);
                        }}
                        options={engagementStatusOptions}
                        value={engagementStatusOptions.find(({ value }) => value === values.engagementStatusId) || null}
                      />
                    </InputWrapper>
                    <InputWrapper
                      label="Start Date"
                      error={
                        touched.transitionDate && typeof errors.transitionDate === 'string' ? errors.transitionDate : ''
                      }
                      isMandatory
                    >
                      <DatePicker
                        customInputIcon={<img className="form-icon" src={calendar} alt="Calendar" />}
                        date={values.transitionDate}
                        dateFormat={REACT_DATEPICKER_DATE_FORMAT}
                        disabled={isSubmitting || isFetchingLeaveData}
                        displayFormat="LL"
                        hasError={Boolean(touched.transitionDate && errors.transitionDate)}
                        isOutsideRange={(date) =>
                          date.isBefore(employee.currentTransitionDate) ||
                          date.isAfter(currentFiscalYear.endDate) ||
                          date.isBefore(currentFiscalYear.startDate) ||
                          (latestEmpStatus?.engagementStatus.name === TERMINATED &&
                            date.isAfter(latestEmpStatus?.transitionDate))
                        }
                        onDateChange={(selectedDate) => {
                          setFieldTouched('transitionDate', true, true);
                          setFieldValue('transitionDate', selectedDate);

                          setEndDate(values.engagementStatusId, setFieldTouched, setFieldValue, selectedDate);
                        }}
                        openDirection="up"
                        placeholderText="Pick a Transition Date"
                        showMonthDropdown={true}
                        showYearDropdown={true}
                      />
                    </InputWrapper>

                    {isTerminatedSelected && employee && (
                      <TerminationRestrictionInfo
                        userFirstName={employee.firstName}
                        userId={employee.id}
                        setIsNotAllowedToTerminate={setIsNotAllowedToTerminate}
                      />
                    )}

                    {values.engagementStatusId === ENGAGEMENT_STATUS_IDS.FIXED_TERM_CONTRACT && (
                      <InputWrapper
                        label="End Date"
                        error={touched.endDate && typeof errors.endDate === 'string' ? errors.endDate : ''}
                        isMandatory
                      >
                        <DatePicker
                          customInputIcon={<img className="form-icon" src={calendar} alt="Calendar" />}
                          date={values.endDate}
                          dateFormat={REACT_DATEPICKER_DATE_FORMAT}
                          disabled={isSubmitting || isFetchingLeaveData}
                          displayFormat="LL"
                          hasError={Boolean(touched.endDate && errors.endDate)}
                          isOutsideRange={(date) =>
                            date.isBefore(employee.currentTransitionDate) ||
                            date.isSameOrBefore(values.transitionDate) ||
                            date.isAfter(currentFiscalYear.endDate) ||
                            date.isBefore(currentFiscalYear.startDate)
                          }
                          onDateChange={(selectedDate) => {
                            setFieldTouched('endDate', true, true);
                            setFieldValue('endDate', selectedDate);
                          }}
                          openDirection="up"
                          placeholderText="Pick an End Date"
                          showMonthDropdown={true}
                          showYearDropdown={true}
                        />
                      </InputWrapper>
                    )}

                    {values.engagementStatusId === ENGAGEMENT_STATUS_IDS.SUB_CONTRACT && (
                      <InputWrapper
                        label="End Date"
                        error={touched.endDate && typeof errors.endDate === 'string' ? errors.endDate : ''}
                        isMandatory={false}
                      >
                        <DatePicker
                          customInputIcon={<img className="form-icon" src={calendar} alt="Calendar" />}
                          date={values.endDate}
                          dateFormat={REACT_DATEPICKER_DATE_FORMAT}
                          disabled={isSubmitting || isFetchingLeaveData}
                          displayFormat="LL"
                          hasError={Boolean(touched.endDate && errors.endDate)}
                          isOutsideRange={(date) => isEndDateOutsideRange(date, values.transitionDate)}
                          onDateChange={(selectedDate) => {
                            setFieldTouched('endDate', false, true);
                            setFieldValue('endDate', selectedDate);
                          }}
                          openDirection="up"
                          placeholderText="Pick an End Date"
                          showMonthDropdown={true}
                          showYearDropdown={true}
                        />
                      </InputWrapper>
                    )}
                    {[ENGAGEMENT_STATUS_IDS.PROBATION, ENGAGEMENT_STATUS_IDS.FIXED_TERM_CONTRACT].includes(
                      values.engagementStatusId
                    ) &&
                      values.transitionDate &&
                      (values.engagementStatusId === ENGAGEMENT_STATUS_IDS.FIXED_TERM_CONTRACT
                        ? values.endDate
                        : !values.endDate) && (
                        <EmployeeStatusDetails
                          values={{
                            engagementStatusId: values.engagementStatusId || undefined,
                            engagementStatusName: values.engagementStatusName || '',
                            transitionDate: values.transitionDate.toDate(),
                          }}
                          setFieldValue={setFieldValue}
                          employee={{ currentTransitionDate: employee.currentTransitionDate }}
                          currentFiscalYear={currentFiscalYear || { startDate: '', endDate: '' }}
                          touched={touched}
                          submitCount={submitCount}
                          errors={errors as { empStatusDetails?: Array<{ category?: string; date?: string }> }}
                        />
                      )}
                    <button
                      className="btn btn--outlined-grey f-left card-button mr-10 mb-20"
                      disabled={isSubmitting || isFetchingLeaveData}
                      onClick={redirectToEmployeeEdit}
                      type="button"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className={classNames('btn f-right card-button', {
                        'btn--primary': !isButtonDisabled,
                        'btn--disabled': isButtonDisabled,
                      })}
                      disabled={isButtonDisabled}
                    >
                      <span>Change</span>
                      {(isSubmitting || isFetchingLeaveData) && <Loading />}
                    </button>
                    {submitCount > 0 && validationMessages.length > 0 && (
                      <div className="alert alert-danger mt-15" role="alert">
                        <div>Please fix the following before continuing:</div>
                        <ul className="mt-10 mb-0 pl-20">
                          {Array.from(new Set(validationMessages)).map((message) => (
                            <li key={message}>{message}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </form>
                );
              }}
            </Formik>
          </div>
        </div>
        <div className="full-scope-card emp-history-area emp-history-wrapper engagement-history">
          <div className="font-bold emp-history-title">Employment Status History</div>
          <div className="leapfrog-history-form__designation-list engagement-history-list custom-scroll-bar">
            {[...employee.empStatusHistory].reverse().map((item) => {
              return (
                <EngagementStatusList
                  canEdit={false}
                  employeeName={employee.name}
                  isCurrent={employee.currentEmpStatusId === item.id}
                  key={item.id}
                  title={item.engagementStatus.name}
                  transitionDate={item.transitionDate}
                  endDate={item.endDate}
                  empStatusDetails={item.empStatusDetails}
                  openEditModal={() => {}}
                  handleDelete={() => {}}
                />
              );
            })}
          </div>
        </div>
      </div>
    </>
  );
};

export default ChangeEngagement;
